/** Inline citations: the agent cites search / knowledge-base results as [1], [2] (see prompt.py). */
import { createContext, useContext } from 'react';

export interface AgentSource {
  index: number;
  title: string;
  url: string | null;
  domain: string | null;
}

export const CITE_HREF_PREFIX = '#aivia-cite-';

/**
 * Numbers shown for sources, without gaps. The agent numbers every search result in a thread and
 * keeps those numbers (so follow-ups can cite them), but an answer cites only some, which left gaps
 * such as 1, 2, 4, 5. Display numbers run 1, 2, 3… across the thread in order of first citation,
 * so a source keeps its shown number in later answers too.
 */
const CITE_LINK = new RegExp(`\\]\\(${CITE_HREF_PREFIX}(\\d+)\\)`, 'g');

export function displayNumbers(answers: Array<{ content: string; sources: AgentSource[] }>): Map<number, number> {
  const out = new Map<number, number>();
  const add = (index: number) => {
    if (!out.has(index)) out.set(index, out.size + 1);
  };
  for (const { content, sources } of answers) {
    const known = new Set(sources.map((s) => s.index));
    // In the order the answer's chips appear (the same parsing linkCitations renders, so [n] in code
    // or links doesn't count), then any listed source it didn't cite inline.
    const linked = linkCitations(content, known);
    for (const m of linked.matchAll(CITE_LINK)) add(Number(m[1]));
    for (const s of sources) add(s.index);
  }
  return out;
}

export const CitationNumbersContext = createContext<Map<number, number> | null>(null);

/** The number to show for source `index` (the agent's own number when there is no mapping). */
export function useShownNumber(): (index: number) => number {
  const numbers = useContext(CitationNumbersContext);
  return (index) => numbers?.get(index) ?? index;
}

/**
 * Uses each source's `index` (the [n] the answer cites; older answers fall back to list position)
 * and only links http(s) URLs.
 */
export function normalizeSources(raw: unknown): AgentSource[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry: any, i) => {
    const index = Number.isInteger(entry?.index) && entry.index > 0 ? entry.index : i + 1;
    let url: string | null = null;
    let domain: string | null = null;
    try {
      const parsed = new URL(String(entry?.url ?? ''));
      if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
        url = parsed.toString();
        domain = parsed.hostname.replace(/^www\./, '');
      }
    } catch {
      /* not a URL */
    }
    const title = typeof entry?.title === 'string' && entry.title.trim() ? entry.title.trim() : domain ?? `Source ${index}`;
    return { index, title, url, domain };
  });
}

// Fenced blocks (closed or still streaming) and inline code are left untouched.
const CODE_SEGMENT = /(```[\s\S]*?(?:```|$)|`[^`\n]*`)/g;
// [n] that is not a link label ([1](url)) or a reference definition ([1]: url).
const CITATION = /\[(\d+)\](?![(:])/g;

/** Turns [n] into markdown links the renderer shows as citation chips, for each n in `indices`. */
export function linkCitations(markdown: string, indices: ReadonlySet<number>): string {
  if (!markdown || !indices.size) return markdown;
  return markdown
    .split(CODE_SEGMENT)
    .map((segment, i) =>
      i % 2 === 1
        ? segment
        : segment.replace(CITATION, (match, n: string) => {
            const index = Number(n);
            return indices.has(index) ? `[${index}](${CITE_HREF_PREFIX}${index})` : match;
          }),
    )
    .join('');
}

export function citationIndex(href: string | undefined): number | null {
  if (!href?.startsWith(CITE_HREF_PREFIX)) return null;
  const n = Number(href.slice(CITE_HREF_PREFIX.length));
  return Number.isInteger(n) && n > 0 ? n : null;
}
